import type { AuthSession } from "./types"

const API_PREFIX = (import.meta.env.VITE_API_PREFIX as string | undefined) ?? "/api"

export class ApiError extends Error {
  readonly status: number
  readonly details: unknown

  constructor(status: number, message: string, details: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.details = details
  }
}

let accessToken: string | null = null
let refreshPromise: Promise<AuthSession> | null = null
const sessionListeners = new Set<(session: AuthSession | null) => void>()

function notifySession(session: AuthSession | null) {
  for (const listener of sessionListeners) listener(session)
}

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken() {
  return accessToken
}

export function subscribeToSession(listener: (session: AuthSession | null) => void) {
  sessionListeners.add(listener)
  return () => {
    sessionListeners.delete(listener)
  }
}

function apiUrl(path: string) {
  if (/^https?:\/\//.test(path)) return path
  return `${API_PREFIX}${path.startsWith("/") ? path : `/${path}`}`
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined
  const contentType = response.headers.get("content-type") ?? ""
  if (contentType.includes("application/json")) return response.json()
  const text = await response.text()
  return text || undefined
}

function errorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === "object" && "message" in payload) {
    const message = (payload as { message: unknown }).message
    if (Array.isArray(message)) return message.map((item) => localizeApiMessage(String(item))).join(". ")
    if (typeof message === "string") return localizeApiMessage(message)
  }
  return localizeApiMessage(fallback)
}

export function localizeApiMessage(message: string) {
  if (/[а-яё]/i.test(message)) return message
  const common: Array<[RegExp, string]> = [
    [/invalid (login|credentials|password)|password is incorrect|unauthorized/i, "Неверный логин или пароль"],
    [/account is suspended/i, "Аккаунт приостановлен. Обратитесь к администратору инстанса"],
    [/second.factor.*required|totp.*required|2fa.*required/i, "Введите код двухфакторной защиты"],
    [/invalid.*(second.factor|totp|2fa|recovery)/i, "Неверный код 2FA или recovery-код"],
    [/insufficient (funds|balance)|not enough/i, "Недостаточно средств"],
    [/last.*(owner|administrator)|at least one owner/i, "В семье должен остаться хотя бы один владелец"],
    [/(member|target|user).*(inactive|suspended)/i, "Этот участник сейчас неактивен"],
    [
      /no.*approver|approver.*not found|no active family member can approve|requires role approvers/i,
      "Нет другого активного участника выбранной роли, который может подтвердить действие"
    ],
    [/there is no balance to collect/i, "На счёте нет средств для взыскания"],
    [/already exists|duplicate|unique constraint/i, "Такая запись уже существует"],
    [/login.*(taken|exists)|user.*already exists/i, "Этот логин уже занят"],
    [
      /invitation.*(invalid|expired|used|revoked)|invalid.*invitation/i,
      "Приглашение недействительно или уже использовано"
    ],
    [/origin.*not allowed|cors/i, "Этот адрес не разрешён настройками сервера"],
    [/forbidden|not allowed|insufficient permissions|cannot .* role/i, "Недостаточно прав для этого действия"],
    [/(daily|weekly).*limit|limit.*exceeded|too many requests/i, "Достигнут установленный лимит. Попробуйте позже"],
    [/not found/i, "Запись не найдена или уже недоступна"],
    [
      /automation.*stale|version is stale|changed concurrently/i,
      "Правило уже изменилось. Обновите страницу и повторите"
    ],
    [/achievement.*(already|once)|not repeatable|cooldown/i, "Эту ачивку сейчас нельзя выдать повторно"],
    [
      /pair.*(issuer|holder|participant|target)|fixed.*pair/i,
      "Для парной валюты действие доступно только назначенному участнику"
    ],
    [/debt.*not allowed|cannot.*debt|balance.*below zero/i, "Правила валюты не разрешают уходить в долг"],
    [
      /request entity too large|payload.*too large|file.*too large/i,
      "Файл слишком большой. Максимальный размер — 50 МБ"
    ],
    [
      /task cannot be edited while.*(submitted|completed)/i,
      "Задачу нельзя изменить, пока результат ожидает проверки или уже принят"
    ],
    [/expired|expiry.*invalid/i, "Срок действия истёк или указан неверно"],
    [
      /item.*(already used|not available|reserved|consumed)|instance.*state/i,
      "Этот предмет уже использован или ожидает решения"
    ],
    [/webhook.*signature|signature.*invalid/i, "Подпись webhook не прошла проверку"],
    [/rule is not a webhook/i, "Это правило не использует webhook"],
    [/cron|schedule.*invalid/i, "Проверьте расписание"],
    [/bad request|validation failed|must be|required/i, "Проверьте заполнение полей"],
    [/service unavailable|internal server error|bad gateway|gateway timeout/i, "Сервер временно недоступен"]
  ]
  return common.find(([pattern]) => pattern.test(message))?.[1] ?? "Не удалось выполнить действие"
}

async function rawRequest<T>(path: string, init: RequestInit = {}, retryAuth = true): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set("Accept", "application/json")
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`)
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json")
  }

  let response: Response
  try {
    response = await fetch(apiUrl(path), { ...init, headers, credentials: "include" })
  } catch (error) {
    throw new ApiError(0, navigator.onLine ? "Сервер пока недоступен" : "Нет подключения к сети", error)
  }

  const authPath = path.split("?", 1)[0] ?? path
  const cannotRefresh = ["/auth/login", "/auth/register", "/auth/refresh"].includes(authPath)
  if (response.status === 401 && retryAuth && !cannotRefresh) {
    try {
      await refreshSession()
      return rawRequest<T>(path, init, false)
    } catch {
      setAccessToken(null)
      notifySession(null)
    }
  }

  const payload = await parseResponse(response)
  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(payload, response.statusText || "Ошибка запроса"), payload)
  }
  // Domain list endpoints intentionally return plain arrays while cursor-based endpoints return
  // `{ items, nextCursor }`. The UI consumes one stable collection shape for both variants.
  return (Array.isArray(payload) ? { items: payload } : payload) as T
}

function jsonBody(body: unknown): BodyInit | undefined {
  return body === undefined ? undefined : JSON.stringify(body)
}

export const api = {
  get<T>(path: string, init?: RequestInit) {
    return rawRequest<T>(path, { ...init, method: "GET" })
  },
  post<T>(path: string, body?: unknown, init?: RequestInit) {
    const payload = jsonBody(body)
    return rawRequest<T>(path, { ...init, method: "POST", ...(payload === undefined ? {} : { body: payload }) })
  },
  patch<T>(path: string, body?: unknown, init?: RequestInit) {
    const payload = jsonBody(body)
    return rawRequest<T>(path, { ...init, method: "PATCH", ...(payload === undefined ? {} : { body: payload }) })
  },
  delete<T>(path: string, init?: RequestInit) {
    return rawRequest<T>(path, { ...init, method: "DELETE" })
  },
  upload<T>(path: string, body: FormData, init?: RequestInit) {
    return rawRequest<T>(path, { ...init, method: "POST", body })
  }
}

export async function refreshSession(): Promise<AuthSession> {
  if (!refreshPromise) {
    refreshPromise = rawRequest<AuthSession>("/auth/refresh", { method: "POST" }, false)
      .then((session) => {
        setAccessToken(session.accessToken)
        notifySession(session)
        return session
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

export function acceptSession(session: AuthSession) {
  setAccessToken(session.accessToken)
  notifySession(session)
}

export function clearSession() {
  setAccessToken(null)
  notifySession(null)
}

export function idempotencyKey(prefix = "web") {
  return `${prefix}-${safeRandomId()}`
}

export function createIdempotencyAttempt(prefix = "web") {
  let key: string | null = null
  let inFlight = false
  return {
    begin() {
      if (inFlight) return null
      inFlight = true
      key ??= idempotencyKey(prefix)
      return key
    },
    finish() {
      inFlight = false
    },
    reset() {
      key = null
      inFlight = false
    }
  }
}

/**
 * `crypto.randomUUID()` is missing in older iOS WebViews and in browsers opened over
 * plain HTTP on a non-local hostname. Idempotency must still work in those contexts.
 */
export function safeRandomId() {
  const browserCrypto = globalThis.crypto
  if (typeof browserCrypto?.randomUUID === "function") return browserCrypto.randomUUID()

  if (typeof browserCrypto?.getRandomValues === "function") {
    const bytes = browserCrypto.getRandomValues(new Uint8Array(16))
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  // Last-resort compatibility path. The server still scopes and validates every operation.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

export function getApiUrl(path: string) {
  return apiUrl(path)
}

export function toApiMessage(error: unknown, fallback = "Что-то пошло не так") {
  return error instanceof ApiError ? error.message : fallback
}
